import React, { useContext, useState, useEffect } from "react";
import AppLayout from "../layouts/AppLayout";
import DataTable from "../components/DataTable";
import { AppContext } from "../contexts/AppContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import Spinner from "react-activity/dist/Spinner";
import "react-activity/dist/Spinner.css";
import Swal from "sweetalert2";
import { HiArrowNarrowRight } from "react-icons/hi";
import { FiPlus } from "react-icons/fi";
import { X } from 'lucide-react'

const EditWebsitesLinks = () => {
  const { webId } = useParams()

  const { getwebsitelinks, getwebsitelink, addwebsitelink, updatewebsitelink, deletewebsitelink, isLoading } =
    useContext(AppContext)

  const [websites, setWebsites] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [link, setLink] = useState('')
  const [iconFile, setIconFile] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)
  const [currentIcon, setCurrentIcon] = useState(null)

  const handleDelete = async (webid) => {
    if (!isLoading) {
      try {
        const result = await Swal.fire({
          title: 'Are you sure to delete?',
          text: "You won't be able to undo this action",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, Delete',
        })

        if (result.isConfirmed) {
          let response = await deletewebsitelink(webid)
          if (response === 'success') {
            fetchWebsites()
          }
        }
      } catch (error) {
        Swal.fire('An Error Occurred', error, 'error')
      }
    }
  }

  const columns = React.useMemo(
    () => [
      {
        Header: 'Icon',
        accessor: 'icon',
        width: 100,
        Cell: ({ row }) => (
          <>
            {row.original.icon ? (
              <img
                src={`${process.env.REACT_APP_STATIC_URL || ''}${row.original.icon}`}
                alt={row.original.name}
                className='w-8 h-8 object-contain'
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            ) : (
              <span className='text-gray-400 text-xs'>No icon</span>
            )}
          </>
        ),
      },
      { Header: 'Name', accessor: 'name' },
      { Header: 'Domains', accessor: 'link' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className='flex items-center space-x-2'>
            {row.original.status !== 'Completed' && (
              <Link
                className='text-edit'
                to={`/edit-websites-links/${row.original.id}`}
              >
                Edit
              </Link>
            )}
            <button className='text-delete' onClick={() => handleDelete(row.original.id)}>
              Delete
            </button>
          </div>
        ),
      },
    ],
    []
  )

  const fetchWebsites = async () => {
    const response = await getwebsitelinks()
    setWebsites(response)
  }

  useEffect(() => {
    const fetchCat = async () => {
      if (webId) {
        const response = await getwebsitelink(webId)
        if (response.length > 0) {
          setName(response[0].name)
          setLink(response[0].link)
          if (response[0].icon) {
            setCurrentIcon(response[0].icon)
            setIconPreview(`${process.env.REACT_APP_STATIC_URL || ''}${response[0].icon}`)
          }
          setIsOpen(true)
        }
      }
    }

    fetchCat()
  }, [webId])

  useEffect(() => {
    fetchWebsites()
  }, [])

  const navigate = useNavigate()

  const handleAddClick = () => {
    setName('')
    setLink('')
    setError('')
    setIconFile(null)
    setIconPreview(null)
    setCurrentIcon(null)
    setIsOpen(true)
    navigate('/edit-websites-links')
  }

  const handleIconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setIconFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setIconPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isLoading) {
      try {
        if (name !== '' && link !== '') {
          setError('')

          let response = ''
          if (webId) {
            response = await updatewebsitelink(name, link, webId, iconFile)
          } else {
            response = await addwebsitelink(name, link, iconFile)
          }
          if (response === 'success') {
            fetchWebsites()
            setName('')
            setLink('')
            setIconFile(null)
            setIconPreview(null)
            setCurrentIcon(null)
            setIsOpen(false)
            navigate('/edit-websites-links')
          }
        } else {
          setError('All fields are required')
          return
        }
      } catch (error) {
        setError('Error: ' + error)
      }
    }
  }

  return (
    <AppLayout pageName='Websites Links'>
      <section>
        {!webId && (
          <div className='flex justify-end flex-wrap mb-4'>
            <div>
              <button
                onClick={handleAddClick}
                className='btn-main-inverse flex items-center gap-2'
              >
                <FiPlus /> Add Website
              </button>
            </div>
          </div>
        )}

        {isOpen && (
          <section className='w-full !mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold text-gray-900'>{webId ? 'Edit Website' : 'Add New Website'}</h3>
              {!webId && (
                <button
                  onClick={() => {
                    setIsOpen(false)
                    setName('')
                    setLink('')
                    setError('')
                    setIconFile(null)
                    setIconPreview(null)
                    setCurrentIcon(null)
                  }}
                  className='text-gray-400 hover:text-gray-600 transition-colors'
                >
                  <X size={20} />
                </button>
              )}
            </div>
            <form
              onSubmit={handleSubmit}
              method='post'
            >
              <div className='mb-4'>
                <label
                  htmlFor='name'
                  className='block text-sm font-semibold text-gray-700 mb-2'
                >
                  Name
                </label>
                <input
                  id='name'
                  name='name'
                  type='text'
                  className='form-control w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  required
                  readOnly={!!webId}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='Enter website name'
                />
              </div>
              <div className='mb-4'>
                <label
                  htmlFor='link'
                  className='block text-sm font-semibold text-gray-700 mb-2'
                >
                  Domains
                </label>
                <input
                  id='link'
                  name='link'
                  type='text'
                  className='form-control w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  required
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder='Enter domain(s)'
                />
              </div>
              <div className='mb-4'>
                <label
                  htmlFor='icon'
                  className='block text-sm font-semibold text-gray-700 mb-2'
                >
                  Icon
                </label>
                <div className='flex items-center gap-4'>
                  <div className='flex-1'>
                    <input
                      id='icon'
                      name='icon'
                      type='file'
                      accept='image/*'
                      onChange={handleIconChange}
                      className='form-control w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'
                    />
                  </div>
                  {(iconPreview || currentIcon) && (
                    <div className='flex-shrink-0'>
                      <img
                        src={iconPreview || `${process.env.REACT_APP_STATIC_URL || ''}${currentIcon}`}
                        alt='Icon preview'
                        className='w-16 h-16 object-contain border border-gray-300 rounded-lg p-2 bg-gray-50'
                      />
                    </div>
                  )}
                </div>
                <p className='mt-2 text-xs text-gray-500'>Upload an icon image for this website (PNG, JPG, etc.)</p>
              </div>
              {error !== '' && <div className='mb-4 text-left text-red-500 font-semibold text-sm'>{error}</div>}
              <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
                {!webId && (
                  <button
                    type='button'
                    onClick={() => {
                      setIsOpen(false)
                      setName('')
                      setLink('')
                      setError('')
                      setIconFile(null)
                      setIconPreview(null)
                      setCurrentIcon(null)
                    }}
                    className='px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all'
                  >
                    Cancel
                  </button>
                )}
                <button
                  type='submit'
                  className='btn-submit flex items-center gap-2 px-5 py-2.5'
                  disabled={isLoading}
                >
                  <span>{webId ? 'Save Changes' : 'Add Website'}</span>
                  <span>
                    {isLoading === false ? (
                      <HiArrowNarrowRight />
                    ) : (
                      <Spinner
                        size={14}
                        className='ml-2'
                        animating={isLoading}
                      />
                    )}
                  </span>
                </button>
              </div>
            </form>
          </section>
        )}

        {isLoading ? (
          <div className='flex justify-center w-full py-12'>
            <Spinner
              color='#CCCCCC'
              size={24}
            />
          </div>
        ) : (
          <DataTable
            data={websites}
            columns={columns}
          />
        )}
      </section>
    </AppLayout>
  )
}

export default EditWebsitesLinks;
